import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/macro';
import { DateTimeRangePickerProps, RelativeTimeOption } from './types';
import { useFormatters } from '@/hooks/useFormatters';
import { useCallback, useEffect, useRef, useState } from 'react';
import { calculateWindow, formatDateTimeRangeLabel, getRelativeTimeValue } from './utils';
import { useAbsoluteDateRange } from './hooks/useAbsoluteDateRange';
import { useRecentRanges } from './hooks/useRecentRanges';
import { WINDOW_RANGES } from '@/constants/dateTimeRangePicker.constants';
import { ClickAwayListener, FormControl, InputLabel, OutlinedInput, Paper, Popper, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-latest';
import { AdapterDayjs } from '@mui/x-date-pickers-latest/AdapterDayjs';
import dayjs from 'dayjs';
import { AbsoluteDatePicker } from './components/AbsoluteDatePicker';
import { RelativeTimeList } from './components/RelativeTimeList';

const DateTimeRangePicker = ({
  value,
  onChange,
  onChangeWindow,
  error,
  fullWidth,
  hideAbsoluteTime,
}: DateTimeRangePickerProps) => {
  const { _, i18n } = useLingui();
  const { formatDateTimeRange } = useFormatters();
  const [openPopper, setOpenPopper] = useState(false);
  const selectedRelativeTime = getRelativeTimeValue(value);
  const [openAbsolute, setOpenAbsolute] = useState(selectedRelativeTime === 'absolute');
  const anchorRef = useRef<HTMLDivElement>(null);

  

  const {
    absoluteDate,
    error: absoluteDateError,
    setError: setAbsoluteDateError,
    updateStart,
    updateStop,
  } = useAbsoluteDateRange(value);
  const { recentRanges, addRange } = useRecentRanges();

  const setSelectedRelativeTime = (relativeTime: RelativeTimeOption) => {
    if (relativeTime.value === 'absolute') {
      setOpenAbsolute(true);
    } else {
      onChange({ start: relativeTime.value, stop: null });
      const { window } = WINDOW_RANGES.find((range) => range.max >= -relativeTime.numericValue)!;
      onChangeWindow?.(window);
      setOpenAbsolute(false);
    }
  };

  const handleApplyAbsoluteRange = useCallback(() => {
    const { start, stop } = absoluteDate;
    const range = {
      start: start.set('seconds', 0).set('milliseconds', 0).toISOString(),
      stop: stop ? stop.toISOString() : null,
    };

    onChange(range);
    addRange(range);

    const window = calculateWindow(start, stop);
    onChangeWindow?.(window);

    setOpenPopper(false);
  }, [absoluteDate, onChange, onChangeWindow, addRange]);

  useEffect(() => {
    if (getRelativeTimeValue(value) === 'absolute') {
      setOpenAbsolute(true);
    } else {
      setOpenAbsolute(false);
    }
  }, [openPopper]);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.locale}>
        <ClickAwayListener onClickAway={() => setOpenPopper(false)}>
          <FormControl focused={openPopper} size="small" fullWidth={fullWidth}>
            <InputLabel htmlFor="time-period" error={Boolean(error)}>
              <Trans comment="Label de selector general de rango de tiempos">Período</Trans>
            </InputLabel>

            <OutlinedInput
              ref={anchorRef}
              error={Boolean(error)}
              readOnly
              value={formatDateTimeRangeLabel(value, i18n, formatDateTimeRange)}
              onFocus={() => {
                setOpenPopper(true);
              }}
              id="time-period"
            />

            <Popper
              open={openPopper}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              sx={{ zIndex: 1400 }}
            >
              <Paper variant="outlined" sx={{ display: 'flex', mt: 0.5, height: 440 }}>
                <RelativeTimeList
                  hideAbsoluteTime={hideAbsoluteTime}
                  openAbsolute={openAbsolute}
                  selectedRelativeTime={selectedRelativeTime}
                  onSelectRelativeTime={setSelectedRelativeTime}
                />

                {openAbsolute && (
                  <AbsoluteDatePicker
                    start={absoluteDate.start}
                    stop={absoluteDate.stop}
                    onStartChange={updateStart}
                    onStopChange={updateStop}
                    onError={setAbsoluteDateError}
                    onApply={handleApplyAbsoluteRange}
                    recentRanges={recentRanges}
                    disabled={Boolean(absoluteDateError)}
                    onSelectRecent={(value) => {
                      updateStart(dayjs(value.start));
                      updateStop(value.stop ? dayjs(value.stop) : null);
                    }}
                  />
                )}
              </Paper>
            </Popper>
          </FormControl>
        </ClickAwayListener>
      </LocalizationProvider>
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
    </>
  );
};


export default DateTimeRangePicker;
export * from './types';
export * from './utils';